import { COLLISION_CELLS_PER_TILE, type EntityInstance } from "daedalus-core";
import type { NpcCell } from "../npcs/marker.ts";
import { carvePatches, cropCellsOf, tilesPerCropCell } from "./field-cells.ts";
import { readFieldFields } from "./field-marker.ts";

/**
 * A rental field's patches as the editor draws them while its marker is selected (ADR-144 decision 6, as amended): the
 * squares the compile carves its painted cells into, each as the collision cells `[column, rowsUp]` a cells field
 * holds, so they are laid on the map from the marker's point as the field's own cells are.
 */

/**
 * The patches a rental field marker carves into, each as its collision cells counted from the marker.
 *
 * @param marker The marker.
 * @param tileSize The world's tile size in pixels.
 * @returns One list of cells per patch, in reading order of the patches; null for a shared field, a marker whose fields
 *   are not valid, or a tile size that does not divide a crop cell.
 */
export function fieldPatchCells(marker: Pick<EntityInstance, "x" | "y" | "fields">, tileSize: number): NpcCell[][] | null {
  const per = tilesPerCropCell(tileSize);
  const read = readFieldFields(marker.fields);
  if (per === null || !read.ok || read.field.kind !== "rental") {
    return null;
  }
  const { patches } = carvePatches(cropCellsOf(marker, read.field.cells, tileSize), read.field.patchSize);
  const side = read.field.patchSize * per * COLLISION_CELLS_PER_TILE;
  const column = Math.floor(marker.x * COLLISION_CELLS_PER_TILE);
  const bottom = Math.floor((marker.y + 1) * COLLISION_CELLS_PER_TILE);
  return patches.map((patch) => {
    const cells: NpcCell[] = [];
    const left = patch.col * per * COLLISION_CELLS_PER_TILE;
    const top = patch.row * per * COLLISION_CELLS_PER_TILE;
    for (let dy = 0; dy < side; dy += 1) {
      for (let dx = 0; dx < side; dx += 1) {
        cells.push([left + dx - column, bottom - 1 - (top + dy)]);
      }
    }
    return cells;
  });
}
